import {Typography} from '@hconnect/uikit'
import {Box, makeStyles, Theme} from '@material-ui/core'
import {AxiosError} from 'axios'
import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {BusinessLineType} from '../../../common/types'
import {
  OrderIntakePayload,
  OrderRequestGroupResponse,
  OrderRequestResponse
} from '../../../OrderIntake/declarations/types'
import {useBranding} from '../../../Organisms/Branding'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {ProcessingOrderCard} from '../ProcessingOrderCard'

import {DeliveryOrderSummaryItem} from './DeliveryOrderSummaryItem'

interface Props {
  isMobile: boolean
  showLoad: boolean
  showTime: boolean
  delivery: OrderIntakePayload
  quantityUom: string
  itemOrder: string
  businessLine: BusinessLineType
  onError: (error: AxiosError) => void
  customErrorMessage?: string
  groupedOrderSummary: OrderRequestGroupResponse | undefined
  isLoading: boolean
  isError: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  errorContainer: {
    background: theme.palette.error.main,
    padding: theme.spacing(2)
  }
}))

export const DeliveryProcessingOrderCard: React.FC<Props> = ({
  isMobile,
  showLoad,
  showTime,
  delivery,
  quantityUom,
  groupedOrderSummary,
  isLoading,
  customErrorMessage,
  onError
}) => {
  const classes = useStyles()
  const {dispatchPhoneNumber} = useBranding()
  const {t} = useTranslation()
  const [orderSummaryData, setOrderSummaryData] = React.useState<OrderRequestResponse | null>(null)
  const [showError, setShowError] = React.useState(false)

  useEffect(() => {
    if (groupedOrderSummary && groupedOrderSummary.isSuccess) {
      setShowError(false)
      setOrderSummaryData(groupedOrderSummary.result as OrderRequestResponse)
    } else if (groupedOrderSummary && !groupedOrderSummary.isSuccess) {
      setShowError(true)
      onError(groupedOrderSummary.result as AxiosError)
    }
  }, [delivery])

  return (
    <>
      {showError ? (
        <ProcessingOrderCard
          shippingType={ShippingType.DELIVER}
          isLoading={isLoading}
          isMobile={isMobile}
          showLoad={showLoad}
          showTime={showTime}
          loadingLabel={t('orderIntake.processingOrder')}
          date={delivery.deliveryDate}
          time={delivery.deliveryTime}
          quantity={delivery.capacity.quantity}
          quantityType={delivery.capacity.quantityType}
          quantityUom={quantityUom}
          haulierInformation={delivery.haulerInfo}
          customerReference={delivery.customerReference}
          driverInstructions={delivery.additionalDriverInfo?.driverInstructions}
          hasError={showError}
          errorMessage={
            <Box className={classes.errorContainer}>
              <Typography variant="h3" customColor="textErrorContrastText">
                {t('orderIntake.orderFailed')}
              </Typography>
              {customErrorMessage ? (
                <Typography variant="body2" customColor="textErrorContrastText">
                  {customErrorMessage}
                </Typography>
              ) : null}
              <Typography variant="h3" customColor="textErrorContrastText">
                {dispatchPhoneNumber
                  ? t('orderIntake.contactByPhone', {phoneNumber: dispatchPhoneNumber})
                  : t('orderIntake.contactByPhoneWithoutNumber')}
              </Typography>
              <Typography variant="body2" customColor="textErrorContrastText">
                {t('orderIntake.errorMessage')}
              </Typography>
            </Box>
          }
        />
      ) : orderSummaryData ? (
        <DeliveryOrderSummaryItem
          isMobile={isMobile}
          showLoad={showLoad}
          orderRequestStatus={orderSummaryData.status}
          quantity={orderSummaryData.requestData?.quantity ?? delivery.capacity.quantity}
          quantityType={delivery.capacity.quantityType}
          quantityUom={orderSummaryData.requestData?.quantityUom ?? quantityUom}
          deliveryDate={orderSummaryData.requestData?.deliveryDate ?? delivery.deliveryDate}
          deliveryTime={orderSummaryData.requestData?.deliveryTime ?? delivery.deliveryTime}
          driverInstructions={
            orderSummaryData.requestData?.additionalDriverInfo.driverInstructions ??
            delivery.additionalDriverInfo?.driverInstructions
          }
          customerReference={
            orderSummaryData.requestData?.customerReference ?? delivery.customerReference
          }
          haulierInformation={orderSummaryData.requestData?.haulerInfo ?? delivery.haulerInfo}
        />
      ) : null}
    </>
  )
}
